import { useEffect, useRef, useState } from 'react';
import { GameProps } from './types';
import { TouchControls } from './TouchControls';

type PongPhase = 'intro' | 'playing' | 'gameover';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function PongGame({ fgColor, bgColor, softColor, onExit }: GameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const stateRef = useRef({
    phase: 'intro' as PongPhase,
    width: 0,
    height: 0,
    deviceRatio: 1,
    paddleHeight: 88,
    paddleWidth: 12,
    paddleMargin: 20,
    playerY: 0,
    aiY: 0,
    ballX: 0,
    ballY: 0,
    ballSize: 12,
    ballVx: 320,
    ballVy: 220,
    playerScore: 0,
    aiScore: 0,
    winner: '' as '' | 'PLAYER' | 'AI',
    upPressed: false,
    downPressed: false,
    lastTime: 0,
  });

  const resetBall = (towardPlayer: boolean) => {
    const state = stateRef.current;
    state.ballX = state.width / 2;
    state.ballY = state.height / 2 + 16;
    const direction = towardPlayer ? -1 : 1;
    state.ballVx = direction * (280 + Math.random() * 90);
    state.ballVy = (Math.random() * 240 - 120) || 80;
  };

  const resetRound = () => {
    const state = stateRef.current;
    state.phase = 'intro';
    state.playerScore = 0;
    state.aiScore = 0;
    state.winner = '';
    state.playerY = state.height / 2 - state.paddleHeight / 2 + 16;
    state.aiY = state.playerY;
    resetBall(false);
    state.lastTime = 0;
  };

  const resizeCanvas = () => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    const bounds = container.getBoundingClientRect();
    const width = Math.max(260, Math.floor(bounds.width));
    const height = Math.max(180, Math.floor(bounds.height));
    const deviceRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(width * deviceRatio);
    canvas.height = Math.floor(height * deviceRatio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const state = stateRef.current;
    state.width = width;
    state.height = height;
    state.deviceRatio = deviceRatio;
    state.paddleHeight = clamp(Math.floor(height * 0.18), 70, 120);
    state.paddleWidth = clamp(Math.floor(width * 0.018), 10, 16);
    state.paddleMargin = clamp(Math.floor(width * 0.03), 16, 26);
    state.ballSize = clamp(Math.floor(height * 0.022), 10, 14);

    resetRound();
  };

  const scorePoint = (playerScored: boolean) => {
    const state = stateRef.current;
    if (playerScored) {
      state.playerScore += 1;
    } else {
      state.aiScore += 1;
    }

    if (state.playerScore >= 5 || state.aiScore >= 5) {
      state.phase = 'gameover';
      state.winner = state.playerScore >= 5 ? 'PLAYER' : 'AI';
      return;
    }

    resetBall(!playerScored);
  };

  const update = (timestamp: number) => {
    const state = stateRef.current;
    if (!state.lastTime) {
      state.lastTime = timestamp;
    }

    const delta = clamp((timestamp - state.lastTime) / 1000, 0, 0.033);
    state.lastTime = timestamp;

    if (state.phase !== 'playing') {
      return;
    }

    const playableTop = 34;
    const playableBottom = state.height - 8;
    const paddleSpeed = 440;

    if (state.upPressed) {
      state.playerY -= paddleSpeed * delta;
    }
    if (state.downPressed) {
      state.playerY += paddleSpeed * delta;
    }
    state.playerY = clamp(state.playerY, playableTop, playableBottom - state.paddleHeight);

    const aiTarget = state.ballY - state.paddleHeight / 2;
    const aiSpeed = 305;
    const aiMove = clamp(aiTarget - state.aiY, -aiSpeed * delta, aiSpeed * delta);
    state.aiY += aiMove;
    state.aiY = clamp(state.aiY, playableTop, playableBottom - state.paddleHeight);

    state.ballX += state.ballVx * delta;
    state.ballY += state.ballVy * delta;

    if (state.ballY <= playableTop || state.ballY + state.ballSize >= playableBottom) {
      state.ballVy *= -1;
      state.ballY = clamp(state.ballY, playableTop, playableBottom - state.ballSize);
    }

    const playerX = state.paddleMargin;
    const aiX = state.width - state.paddleMargin - state.paddleWidth;

    const hitsPlayerPaddle =
      state.ballX <= playerX + state.paddleWidth &&
      state.ballX + state.ballSize >= playerX &&
      state.ballY + state.ballSize >= state.playerY &&
      state.ballY <= state.playerY + state.paddleHeight;

    if (hitsPlayerPaddle && state.ballVx < 0) {
      state.ballX = playerX + state.paddleWidth + 1;
      state.ballVx = Math.abs(state.ballVx) * 1.03;
      const offset = (state.ballY + state.ballSize / 2 - (state.playerY + state.paddleHeight / 2)) / (state.paddleHeight / 2);
      state.ballVy += offset * 90;
    }

    const hitsAiPaddle =
      state.ballX + state.ballSize >= aiX &&
      state.ballX <= aiX + state.paddleWidth &&
      state.ballY + state.ballSize >= state.aiY &&
      state.ballY <= state.aiY + state.paddleHeight;

    if (hitsAiPaddle && state.ballVx > 0) {
      state.ballX = aiX - state.ballSize - 1;
      state.ballVx = -Math.abs(state.ballVx) * 1.03;
      const offset = (state.ballY + state.ballSize / 2 - (state.aiY + state.paddleHeight / 2)) / (state.paddleHeight / 2);
      state.ballVy += offset * 90;
    }

    if (state.ballX + state.ballSize < 0) {
      scorePoint(false);
    } else if (state.ballX > state.width) {
      scorePoint(true);
    }
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const state = stateRef.current;

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.setTransform(state.deviceRatio, 0, 0, state.deviceRatio, 0, 0);

    context.fillStyle = bgColor;
    context.fillRect(0, 0, state.width, state.height);

    context.fillStyle = fgColor;
    context.font = '16px "Courier New", monospace';
    context.fillText(`PONG  ${state.playerScore} : ${state.aiScore}`, 8, 22);

    context.globalAlpha = 0.24;
    for (let y = 36; y < state.height - 8; y += 18) {
      context.fillRect(state.width / 2 - 2, y, 4, 10);
    }
    context.globalAlpha = 1;

    context.fillRect(state.paddleMargin, state.playerY, state.paddleWidth, state.paddleHeight);
    context.fillRect(state.width - state.paddleMargin - state.paddleWidth, state.aiY, state.paddleWidth, state.paddleHeight);
    context.fillRect(state.ballX, state.ballY, state.ballSize, state.ballSize);

    if (state.phase === 'intro') {
      context.fillStyle = softColor;
      context.font = '15px "Courier New", monospace';
      context.fillText('Use UP/DOWN arrows. First to 5 wins. Press ENTER to start. ESC quits.', 8, state.height - 12);
    }

    if (state.phase === 'gameover') {
      context.fillStyle = 'rgba(0, 0, 0, 0.6)';
      context.fillRect(0, 0, state.width, state.height);
      context.fillStyle = fgColor;
      context.font = '24px "Courier New", monospace';
      context.fillText(`${state.winner} WINS`, Math.max(8, state.width / 2 - 90), state.height / 2 - 10);
      context.font = '16px "Courier New", monospace';
      context.fillStyle = softColor;
      context.fillText(`Final: ${state.playerScore} - ${state.aiScore}`, Math.max(8, state.width / 2 - 70), state.height / 2 + 18);
      context.fillText('Press ENTER to restart or ESC to quit', Math.max(8, state.width / 2 - 158), state.height / 2 + 44);
    }
  };

  const handleTouchDirection = (direction: 'up' | 'down') => {
    const state = stateRef.current;

    if (state.phase === 'gameover') {
      resetRound();
      state.phase = 'playing';
    } else if (state.phase === 'intro') {
      state.phase = 'playing';
    }

    if (state.phase !== 'playing') {
      return;
    }

    const playableTop = 34;
    const playableBottom = state.height - 8;
    const moveStep = clamp(Math.floor(state.height * 0.07), 14, 34);
    const nextY = direction === 'up' ? state.playerY - moveStep : state.playerY + moveStep;
    state.playerY = clamp(nextY, playableTop, playableBottom - state.paddleHeight);
  };

  const handleTouchQuit = () => {
    const state = stateRef.current;
    onExit(`${state.playerScore}-${state.aiScore}`);
  };

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    resizeCanvas();

    const observer = new ResizeObserver(() => resizeCanvas());
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    const tick = (timestamp: number) => {
      update(timestamp);
      draw();
      animationRef.current = window.requestAnimationFrame(tick);
    };

    animationRef.current = window.requestAnimationFrame(tick);

    const handleKeyDown = (event: KeyboardEvent) => {
      const state = stateRef.current;
      const key = event.key;

      if (key === 'Escape') {
        event.preventDefault();
        onExit(`${state.playerScore}-${state.aiScore}`);
        return;
      }

      if (key === 'ArrowUp') {
        event.preventDefault();
        state.upPressed = true;
      }

      if (key === 'ArrowDown') {
        event.preventDefault();
        state.downPressed = true;
      }

      if ((state.phase === 'intro' || state.phase === 'gameover') && key === 'Enter') {
        event.preventDefault();
        if (state.phase === 'gameover') {
          resetRound();
        }
        state.phase = 'playing';
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        stateRef.current.upPressed = false;
      }
      if (event.key === 'ArrowDown') {
        stateRef.current.downPressed = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      observer.disconnect();
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationRef.current) {
        window.cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="game-canvas-wrap" ref={containerRef}>
      {isTouchDevice ? <div className="game-touch-instruction">Touch: hold UP/DOWN to move. Tap QUIT to exit.</div> : null}
      <canvas ref={canvasRef} className="game-canvas" />
      {isTouchDevice ? (
        <TouchControls
          type="updown"
          onDirection={(direction) => {
            if (direction === 'up' || direction === 'down') {
              handleTouchDirection(direction);
            }
          }}
          onAction={() => {}}
          onQuit={handleTouchQuit}
        />
      ) : null}
    </div>
  );
}
